//import * as Sentry from "@sentry/browser";
import {
  AppContainer,
  AppLibrary,
  AuthTokenObj,
  TrackingData,
  TrackingUser,
} from "./types";

const pageMapping: { [key: string]: string } = {
  "/": "initializing",
  "/loading": "initializing",
  "/place-input": "placeInputScreen",
  "/map": "mapScreen",
  "/streetview": "streetviewScreen",
  "/to-the-door": "toTheDoorScreen",
};

class OkHiTracker {
  private user?: TrackingUser;
  private authTokenObj?: AuthTokenObj;
  private appContainer?: AppContainer;
  private appLibrary?: AppLibrary;
  private appPlatform?: { name?: string; version?: string };
  private isStreetviewEnabled?: boolean;
  private isToTheDoorEnabled?: boolean;

  buildConnectionContextPayload = () => {
    // @ts-ignore
    if (navigator.connection) {
      const { type, downlink, rtt, downlinkMax, effectiveType, saveData } =
      // @ts-ignore
        navigator.connection as any;
      return {
        type,
        effectiveType,
        saveData,
        downlink: downlink ? `${downlink} Mb/s` : undefined,
        rtt: rtt ? `${rtt}ms` : undefined,
        downlinkMax: downlinkMax ? `${downlinkMax} Mb/s` : undefined,
      };
    }
    return undefined;
  };

  buildBasePayload = () => {
    const environment = process.env.REACT_APP_ENVIRONMENT;
    const appName = process.env.REACT_APP_NAME;
    const appVersion = process.env.REACT_APP_VERSION;
    return {
      environment,
      firstName: this.user?.firstName || undefined,
      lastName: this.user?.lastName || undefined,
      phone: this.user?.phonenumber || undefined,
      userId: this.user?.userId || undefined,
      context: {
        isStreetviewEnabled: this.isStreetviewEnabled,
        isToTheDoorEnabled: this.isToTheDoorEnabled,
        productSuite: {
          name: "OkCollect",
        },
        ...(this.authTokenObj && {
          branch: {
            name: this.authTokenObj.branch.name,
            id: this.authTokenObj.branch.id,
          },
          org: {
            name: this.authTokenObj.app.org.username,
            id: this.authTokenObj.app.org.id,
          },
          app: {
            name: this.authTokenObj.app.name,
            id: this.authTokenObj.app.id,
          },
        }),
        developer: {
          name: environment === "development" ? "okhi" : "external",
        },
        ...(this?.appPlatform && {
          platform: {
            name: this.appPlatform.name || undefined,
            version: this.appPlatform.version || undefined,
          },
        }),
        ...(this?.appContainer && {
          container: {
            name: this.appContainer.name || undefined,
            version: this.appContainer.version || undefined,
          },
        }),
        ...(this?.appLibrary && {
          library: {
            name: this.appLibrary.name || undefined,
            version: this.appLibrary.version || undefined,
          },
        }),
        component: {
          name: appName,
          version: appVersion,
        },
      },
    };
  };

  buildContext = () => {
    const appName = process.env.REACT_APP_NAME;
    const appVersion = process.env.REACT_APP_VERSION;
    return {
      app: {
        name: appName,
        version: appVersion,
      },
      ...(this.appPlatform && {
        os: {
          name: this.appPlatform.name || undefined,
          version: this.appPlatform.version || undefined,
        },
      }),
      network: this.buildConnectionContextPayload(),
    };
  };

  enrich = (
    trackingData: TrackingData,
    isStreetviewEnabled: boolean,
    isToTheDoorEnabled: boolean
  ) => {
    this.isStreetviewEnabled = isStreetviewEnabled;
    this.isToTheDoorEnabled = isToTheDoorEnabled;
    if (trackingData.user) {
      if (
        trackingData.user.userId &&
        this.user?.userId !== trackingData.user.userId
      ) {
        this.user = {
          ...this.user,
          ...trackingData.user,
        };
        this.identify();
      } else {
        this.user = {
          ...this.user,
          ...trackingData.user,
        };
      }
    }
    if (trackingData.authTokenObj) {
      this.authTokenObj = trackingData.authTokenObj;
    }
    if (trackingData.appContainer) {
      this.appContainer = {
        ...this.appContainer,
        ...trackingData.appContainer,
      };
    }
    if (trackingData.appLibrary) {
      this.appLibrary = {
        ...this.appLibrary,
        ...trackingData.appLibrary,
      };
    }
    if (trackingData.appPlatform) {
      this.appPlatform = {
        ...this.appPlatform,
        ...trackingData.appPlatform,
      };
    }
  };

  track = (eventName: string, properties: Object) => {
    const environment = process.env.REACT_APP_ENVIRONMENT;
    try {
      const view = pageMapping[window.location.pathname];
      const payload = {
        ...this.buildBasePayload(),
        ...properties,
        view,
      };
      if (this.user?.userId) {
        this.identify();
      }
      if ((window as any).analytics) {
        (window as any).analytics.track(eventName, payload, this.buildContext());
      }
    } catch (error) {
      if (environment === "development") {
        console.error("Portal: error tracking data", error);
      }
    }
  };

  identify = () => {
    const environment = process.env.REACT_APP_ENVIRONMENT;
    const userId = this.user?.userId;
    const analytics = (window as any).analytics;

    if (userId && analytics) {
      try {
        //@ts-ignore
        window.analytics.identify(
          userId,
          {
            firstName: this.user?.firstName || undefined,
            lastName: this.user?.lastName || undefined,
            phone: this.user?.phonenumber || undefined,
          },
          this.buildContext()
        );
      } catch (error) {
        if (environment === "development") {
          console.error("Portal: error identifying user");
        }
      }
    }
  };

  page = () => {
    try {
      const pageName = pageMapping[window.location.pathname];
      const payload = {
        ...this.buildBasePayload(),
        ...window.location,
      };
      const analytics = (window as any).analytics;

     if (analytics) {
        //@ts-ignore
        analytics.page(pageName, payload, this.buildContext());
      }
    } catch (error) {
      console.error("Portal: error identifying page");
    }
  };
}

const okhiTracker = new OkHiTracker();

export { okhiTracker };
